'use client';

import { useTransactionsContext } from './TransactionsContext';
import { EmptyTransactionsList } from './components/EmptyTransactionsList';
import { MonthSection } from './components/MonthSection';
import { OlderTransactionsSection } from './components/OlderTransactionsSection';
import { TransactionsLoadingSkeleton } from './components/TransactionsLoadingSkeleton';
import { useMonthVisibilityObserver } from './hooks/useMonthVisibilityObserver';
import type { MonthKey } from './types';

type TransactionsProps = {
  onVisibleMonthChange: (monthKey: MonthKey) => void;
};

function Transactions({ onVisibleMonthChange }: TransactionsProps) {
  const { namespace, isInitialLoading, isEmpty, last12Months } = useTransactionsContext();
  const { registerWithObserver } = useMonthVisibilityObserver({ last12Months, onVisibleMonthChange });

  if (isInitialLoading) {
    return <TransactionsLoadingSkeleton />;
  }

  // NOTE: This top-level empty state is displayed when there are no transactions at all for the last
  // 12 months. It is also used if there was an error fetching transactions. Using an empty state for
  // an error state is not ideal, but there are no current designs for this. This should be considered
  // in future iterations.
  if (isEmpty) {
    return <EmptyTransactionsList namespace={namespace} />;
  }

  return (
    <div data-testid="transactions-list" className="flex flex-col gap-6">
      {last12Months.map((month: MonthKey) => {
        return <MonthSection key={month} month={month} registerWithParentObserver={registerWithObserver} />;
      })}
      <OlderTransactionsSection />
    </div>
  );
}

export { Transactions };
